/**
 * mcp-meta.ts — MCP 元工具（渐进式披露 / Progressive Disclosure）
 *
 * 背景：当用户启用的 MCP 服务器工具总数很多时，全量注入会费 token、拖慢、
 * 且工具越多模型越易选错。业界成熟做法（Anthropic 官方 MCP 客户端最佳实践）
 * 是「元工具化」：平时只暴露极少数元工具，模型先浏览轻量目录，再按需拉取
 * 单个服务器的工具详情，最后通过通用调度器执行。
 *
 * 本模块按「服务器」为披露颗粒度，提供：
 *   - list_mcp_servers   浏览已启用服务器的轻量目录（不含 schema）
 *   - load_mcp_tools     拉取【单个】服务器全部工具的详情（name/description/schema）
 *   - call_mcp_tool      通用调度器，委托 useMCPClient().callTool 执行真实工具
 *   - remove_mcp_server  删除服务器（执行前经用户二次确认）
 *
 * 组装策略（assembleMCPToolset）：只要有启用的服务器，就统一注入元工具
 *（披露工具 list/load/call + 管理工具 remove），永不全量注入真实工具。
 * 无启用服务器时返回空集，不影响普通文本请求。
 */

import type { MCPServerConfig } from '../stores/mcp'
import type { RawMCPTool } from './mcp-servers'

import { useMCPClient } from '../composables/useMCPClient'
import { useGlobalConfirmStore } from '../stores/global-confirm'
import { useMCPStore } from '../stores/mcp'

/** 组装模式 */
export type MCPToolMode = 'meta' | 'none'

export interface MCPToolset {
  tools: RawMCPTool[]
  mode: MCPToolMode
  /** 已启用服务器的工具定义总数 */
  totalToolCount: number
  /** 已启用服务器数量 */
  serverCount: number
}

/**
 * 按引用（id 优先，其次 name）定位一个启用/存在的服务器。
 * 返回 undefined 表示未找到。
 */
function resolveServer(
  servers: MCPServerConfig[],
  ref: string,
): MCPServerConfig | undefined {
  const key = ref.trim()
  // 先按 id 精确匹配
  const byId = servers.find(s => s.id === key)
  if (byId)
    return byId
  // 再按 name 匹配（忽略大小写与首尾空白）
  const lower = key.toLowerCase()
  return servers.find(s => s.name.trim().toLowerCase() === lower)
}

// ============================================================
// 管理工具：list_mcp_servers / remove_mcp_server
// 只要有启用服务器就常驻（统一注入 meta 工具），
// 让 AI 能看到已有配置、避免重复添加，并可在确认后删除。
// ============================================================

/** list_mcp_servers：返回已启用服务器的轻量目录（不含完整 schema） */
const listServersTool: RawMCPTool = {
  function: {
    name: 'list_mcp_servers',
    description: '列出当前已启用的 MCP 服务器目录（名称、用途、包含的工具名）。在配置新 MCP 前应先调用此工具，避免重复添加已存在的服务器；需要使用某服务器的工具时，也先用它查看有哪些服务器。',
    parameters: { type: 'object', properties: {} },
  },
  execute: async (): Promise<string> => {
    const mcpStore = useMCPStore()
    const servers = mcpStore.enabledServers
    if (servers.length === 0)
      return '当前没有已启用的 MCP 服务器。'

    const lines = servers.map((s) => {
      const toolNames = (s.cachedTools ?? []).map(t => t.name).join(', ') || '（暂无缓存工具，可能需要同步）'
      const desc = s.description?.trim() ? `：${s.description.trim()}` : ''
      return `- ${s.name}（id: ${s.id}）${desc}｜工具：${toolNames}`
    })
    return `已启用的 MCP 服务器（共 ${servers.length} 个）：\n${lines.join('\n')}`
  },
}

/** load_mcp_tools：拉取【单个】服务器全部工具的详情（name/description/inputSchema） */
const loadToolsTool: RawMCPTool = {
  function: {
    name: 'load_mcp_tools',
    description: '拉取【单个】MCP 服务器包含的全部工具的详细定义（工具名、描述、参数 schema）。当你确定要用某个服务器的能力时调用它，拿到 schema 后再用 call_mcp_tool 执行具体工具。server 可传服务器名称或 id。',
    parameters: {
      type: 'object',
      properties: {
        server: { type: 'string', description: '目标 MCP 服务器的名称或 id（可先用 list_mcp_servers 查看）' },
      },
      required: ['server'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { server } = (args ?? {}) as { server?: string }
    if (!server || !server.trim())
      return '缺少参数 server。请先用 list_mcp_servers 查看可用服务器，再传入服务器名称或 id。'

    const mcpStore = useMCPStore()
    const target = resolveServer(mcpStore.enabledServers, server)
    if (!target)
      return `未找到已启用的 MCP 服务器「${server}」。请先用 list_mcp_servers 查看可用服务器。`

    const tools = target.cachedTools ?? []
    if (tools.length === 0)
      return `服务器「${target.name}」暂无缓存工具，可能需要到 设置 → 机体模块 → 工具 (MCP) 里点「同步工具」。`

    const detail = tools.map((t) => {
      const schema = JSON.stringify(t.inputSchema ?? { type: 'object', properties: {} })
      return `• ${t.name}：${t.description || '（无描述）'}\n  参数 schema：${schema}`
    }).join('\n')
    return `服务器「${target.name}」（id: ${target.id}）共 ${tools.length} 个工具：\n${detail}\n\n用 call_mcp_tool 执行，例如 call_mcp_tool({ server: "${target.id}", toolName: "${tools[0].name}", args: { ... } })。`
  },
}

/** call_mcp_tool：通用调度器，委托 useMCPClient().callTool 执行真实工具 */
const callToolTool: RawMCPTool = {
  function: {
    name: 'call_mcp_tool',
    description: '执行某个 MCP 服务器上的一个具体工具。若服务器、toolName 和参数 schema 已从上下文或之前结果中明确获知，可直接调用；否则先用 list_mcp_servers / load_mcp_tools 确认。server 传服务器名称或 id，toolName 传工具名，args 传符合 schema 的参数对象。',
    parameters: {
      type: 'object',
      properties: {
        server: { type: 'string', description: '目标 MCP 服务器的名称或 id' },
        toolName: { type: 'string', description: '要执行的工具名（来自 load_mcp_tools 的结果）' },
        args: { type: 'object', description: '工具入参对象，需符合该工具的参数 schema' },
      },
      required: ['server', 'toolName'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { server, toolName, args: toolArgs } = (args ?? {}) as {
      server?: string
      toolName?: string
      args?: Record<string, unknown>
    }
    if (!server || !server.trim())
      return '缺少参数 server。请先用 list_mcp_servers 查看可用服务器。'
    if (!toolName || !toolName.trim())
      return '缺少参数 toolName。请先用 load_mcp_tools 查看该服务器的工具名。'

    const mcpStore = useMCPStore()
    const target = resolveServer(mcpStore.enabledServers, server)
    if (!target)
      return `未找到已启用的 MCP 服务器「${server}」。请先用 list_mcp_servers 查看可用服务器。`

    // 基本结构校验：args 若提供必须是对象（非数组、非 null）
    if (toolArgs !== undefined
      && (typeof toolArgs !== 'object' || toolArgs === null || Array.isArray(toolArgs))) {
      return 'args 必须是一个参数对象（key-value）。请对照 load_mcp_tools 返回的 schema 重新组织参数。'
    }

    try {
      return await useMCPClient().callTool(target, toolName.trim(), (toolArgs ?? {}) as Record<string, unknown>)
    }
    catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      console.warn('[call_mcp_tool] 执行失败:', error)
      return `调用工具「${toolName}」失败：${msg}`
    }
  },
}

/** remove_mcp_server：删除服务器，执行前经用户二次确认（window.confirm） */
const removeServerTool: RawMCPTool = {
  function: {
    name: 'remove_mcp_server',
    description: '删除一个已配置的 MCP 服务器。用于清理重复或不再需要的配置。执行时会弹出确认框请用户二次确认，用户拒绝则不删除。server 传服务器名称或 id。',
    parameters: {
      type: 'object',
      properties: {
        server: { type: 'string', description: '要删除的 MCP 服务器的名称或 id' },
      },
      required: ['server'],
    },
  },
  execute: async (args: unknown): Promise<string> => {
    const { server } = (args ?? {}) as { server?: string }
    if (!server || !server.trim())
      return '缺少参数 server。请先用 list_mcp_servers 查看可用服务器。'

    const mcpStore = useMCPStore()
    // 删除针对所有服务器（含未启用），避免「禁用后删不掉」
    const target = resolveServer(mcpStore.servers, server)
    if (!target)
      return `未找到 MCP 服务器「${server}」，无需删除。`

    // 二次确认：避免误删带 token 的配置。弹 MeowChat 风格确认框并 await 用户选择。
    const confirmed = await useGlobalConfirmStore().confirm({
      title: '删除 MCP 服务器',
      message: `AI 请求删除「${target.name}」，删除后其工具和 API Key 配置将一并移除。确认删除？`,
      confirmText: '删除',
      cancelText: '保留',
      confirmVariant: 'danger',
    })
    if (!confirmed)
      return `用户取消了对「${target.name}」的删除操作，配置已保留。`

    mcpStore.removeServer(target.id)
    return `✅ 已删除 MCP 服务器「${target.name}」。`
  },
}

/** 元工具全集：披露工具（list/load/call）+ 管理工具（remove） */
const metaTools: RawMCPTool[] = [listServersTool, loadToolsTool, callToolTool, removeServerTool]

/**
 * 组装本次请求要注入的 MCP 工具集。
 *
 * - 无启用服务器 → mode='none'，返回空数组。
 * - 有启用服务器 → mode='meta'，统一注入元工具（永不全量注入真实工具）。
 */
export async function assembleMCPToolset(): Promise<MCPToolset> {
  const mcpStore = useMCPStore()
  const servers = mcpStore.enabledServers
  const serverCount = servers.length
  const totalToolCount = servers.reduce((sum, s) => sum + (s.cachedTools?.length ?? 0), 0)

  if (serverCount === 0)
    return { tools: [], mode: 'none', totalToolCount: 0, serverCount: 0 }

  return { tools: [...metaTools], mode: 'meta', totalToolCount, serverCount }
}
